import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { DrawingService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import { Feature } from 'ol';
import { Geometry, Polygon } from 'ol/geom';
import { Subscription, take } from 'rxjs';
import { DXFMapComponent } from '../components/dxf-map/dxf-map.component';
import { MapService } from './map.service';

/**
 * Service for handling the dxf tool
 */
@Injectable({
  providedIn: 'root'
})
export class DXFService {
  // reference to the dialog menu
  private _dialogRef: MatDialogRef<DXFMapComponent>;

  public drawingToolSubscription: Subscription;

  /** Il dialog DXF è un tool "strong" del click sulla mappa per tutta la sua vita, tranne mentre il disegno ne è il sotto-passo */
  private static readonly CLICK_OWNER_ID = 'dxf';
  private _drawingClosedSubscription: Subscription;

  public dxfAreaSelected: Feature<Geometry> = null;

  constructor(
    private _dialog: MatDialog,
    private _mapService: MapService,
    private _ebwModalSrv: EbwModalService,
    private _i18nService: I18NService,
    private drawingToolService: DrawingService,
    private utilsService: UtilsService,
    private apiUrlService: ApiUrlService
  ) {}

  /**
   * method to open the plotting tool
   * @returns
   */
  public openDXF() {
    // c'è già uno strumento aperto: il dialog non si apre (il registro mostra l'errore)
    if (!this._mapService.acquireMapClick({ id: DXFService.CLICK_OWNER_ID, level: 'strong' })) {
      return;
    }

    if (this.drawingToolSubscription) {
      this.drawingToolSubscription.unsubscribe();
    }
    this.drawingToolSubscription = this.drawingToolService.drawingGeometry$.subscribe({
      next: (feature) => {
        this.dxfAreaSelected = feature;
        // il dialog viene ripristinato alla chiusura del disegno (_resumeAfterDrawing)
        this.drawingToolService.closeDrawingTool();
      },
      error: (error) => console.error('[DXFService][openDXF]', error)
    });

    // create the dialog window
    this._dialogRef = this._dialog.open(DXFMapComponent, {
      disableClose: true,
      hasBackdrop: true
    });

    // routine to execute after the tool is closed, cleaning all the variables and the map
    this._dialogRef.afterClosed().subscribe((_: any) => {
      this._dialogRef = null;
      this._drawingClosedSubscription?.unsubscribe();
      this._mapService.releaseMapClick(DXFService.CLICK_OWNER_ID);
    });
  }

  public closeDXF() {
    if (this.drawingToolSubscription) {
      this.drawingToolSubscription.unsubscribe();
      this.dxfAreaSelected = null;
    }
  }

  /**
   * method to start the selection of the area for plotting
   * @param settings various settings used for different types of drawing
   */
  public selectArea() {
    this.dxfAreaSelected = null;

    // il disegno è un sotto-passo del DXF: il DXF passa il click al disegno e lo riprende quando il disegno si chiude
    this._mapService.releaseMapClick(DXFService.CLICK_OWNER_ID);
    const opened = this.drawingToolService.openDrawingTool({
      point: false,
      line: false,
      polygon: true,
      circle: false,
      text: false
    });
    // non dovrebbe succedere (il click è appena stato rilasciato): il dialog resta visibile e il DXF riprende il click
    if (!opened) {
      this._mapService.acquireMapClick({ id: DXFService.CLICK_OWNER_ID, level: 'strong' });
      return;
    }

    // chiusura del disegno, con o senza geometria: il dialog DXF torna visibile
    this._drawingClosedSubscription?.unsubscribe();
    this._drawingClosedSubscription = this.drawingToolService.drawingToolClosed$.pipe(take(1)).subscribe(() => this._resumeAfterDrawing());

    // remove the backdrop and the menu
    document.querySelector('.cdk-overlay-backdrop')?.classList.add('disappear');
    this._dialogRef.addPanelClass('disappear');
  }

  private _resumeAfterDrawing(): void {
    // il dialog può essere stato chiuso nel frattempo
    if (!this._dialogRef) {
      return;
    }
    this._mapService.acquireMapClick({ id: DXFService.CLICK_OWNER_ID, level: 'strong' });
    this._dialogRef.removePanelClass('disappear');
    document.querySelector('.cdk-overlay-backdrop')?.classList.remove('disappear');
  }

  public isDXFOpen(): boolean {
    return this._dialogRef ? true : false;
  }

  public save(title: string): void {
    // let activeBaseLayers = '';

    // for (let [_, value] of this._mapService.activeLayers.entries()) {
    //   if (value.get(MapService.GROUP_LAYER_KEY) == 'MAPSERVICE' && value.getProperties().visible == true) {
    //     console.log('FILO, ho modificato qui perchè la chiave adesso è uuid e non il nome del layer');
    //     activeBaseLayers += value.get(MapService.LABEL_LAYER_KEY) + ',';
    //   }
    // }

    // activeBaseLayers = activeBaseLayers.substring(0, activeBaseLayers.length - 1);

    // if (this.dxfAreaSelected == null) {
    //   this._ebwModalSrv.openErrorModal({
    //     title: this._i18nService.getInstantValue('common.error'),
    //     content: this._i18nService.getInstantValue('map.export_dxf.error.missing_geometry')
    //   });

    //   return;
    // }

    // if (activeBaseLayers.length == 0) {
    //   this._ebwModalSrv.openErrorModal({
    //     title: this._i18nService.getInstantValue('common.error'),
    //     content: this._i18nService.getInstantValue('map.export_dxf.error.missing_layers')
    //   });

    //   return;
    // }

    let coordinates = (this.dxfAreaSelected.getGeometry() as Polygon)
      .clone()
      .transform(MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY)
      .getCoordinates()[0];
    if (coordinates.length < 5) {
      this._ebwModalSrv.openErrorModal({
        title: this._i18nService.getInstantValue('common.error'),
        content: this._i18nService.getInstantValue('map.export_dxf.error.not_valid_polygon')
      });

      return;
    }

    const body = {
      title: title,
      coordinates: coordinates
      // layers: activeBaseLayers
    };

    this.utilsService.postRequest(this.apiUrlService.createUrl(API_URLS.MAP.EXPORT.DXF), body, false, {}, 'application/json').subscribe({
      next: (_) => {
        this._ebwModalSrv.openSuccessModal({
          title: this._i18nService.getInstantValue('map.export_dxf.export_dxf_success'),
          content: this._i18nService.getInstantValue('map.export_dxf.export_dxf_success_description')
        });

        this._dialogRef.close();
      },
      error: (err) => {
        console.error('[DXFService][save]', err);
        this._ebwModalSrv.openErrorModal({
          title: this._i18nService.getInstantValue('common.error'),
          content: this._i18nService.getInstantValue('map.export_dxf.export_dxf_error')
        });
      }
    });
  }
}
